import React, { FC } from 'react';
import { RepairOrder } from '@packages/models/api';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Typography } from '@packages/ui/shared';
import { getDisplayDate } from '@web/utils';
import { DMSJobDetail } from './dms-job-detail';

export interface DMSDetailContentProps {
    latestRepairOrder: RepairOrder | null;
}

const useDMSDetailStyles = createUseStyles({
    title: {
        display: 'flex',
        flex: 1,
        padding: '24px 30px',
    },
    content: {
        padding: '0px 30px',
    },
    flexBasis50: {
        flexBasis: '50%',
    },
    labelValuePair: {
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
    },
    detailPairs: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: 32,
        rowGap: 12,
    },
});

export const DMSDetailContent: FC<DMSDetailContentProps> = ({ latestRepairOrder }) => {
    const { t } = useTranslation();
    const classes = useDMSDetailStyles();

    const basicDetails = React.useMemo(
        () => [
            { label: t('Open Date'), value: getDisplayDate(latestRepairOrder?.openDate) },
            { label: t('Closed Date'), value: getDisplayDate(latestRepairOrder?.closedDate) },
            { label: t('RO Mileage'), value: latestRepairOrder?.mileage },
        ],
        [t, latestRepairOrder]
    );

    const warrantyDetails = React.useMemo(
        () => [
            { label: t('Warranty Company'), value: latestRepairOrder?.warrantyCompany || 'N/A' },
            {
                label: t('Warranty Expiration Date'),
                value: getDisplayDate(latestRepairOrder?.warrantyExpirationDate) || 'N/A',
            },
            { label: t('Warranty Expiration Miles'), value: latestRepairOrder?.warrantyExpirationMile || 'N/A' },
            { label: t('Warranty Pay Date'), value: getDisplayDate(latestRepairOrder?.warrantyPayDate) || 'N/A' },
        ],
        [t, latestRepairOrder]
    );

    return (
        <>
            <div className={classes.title}>
                <Typography variant="h3">{t('Repair Order Detail')}</Typography>
            </div>
            <div className={classes.content}>
                <div className={classes.detailPairs}>
                    {basicDetails.map(({ label, value }, index) => (
                        <div key={index} className={`d-flex flex-column pb-6 ${classes.labelValuePair}`}>
                            <div>
                                <Typography variant="label">{label}</Typography>
                            </div>
                            <div className="pt-1">
                                <Typography>{value}</Typography>
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ border: '1px solid #E3E3E3' }} />
            </div>
            <DMSJobDetail
                details={(latestRepairOrder?.details ?? []).map((detail) => ({
                    ...detail,
                    cause: detail.cause ?? null,
                }))}
            />
            <div className={classes.content}>
                <div className={classes.detailPairs}>
                    {warrantyDetails.map(({ label, value }, index) => (
                        <div key={index} className={`d-flex flex-column pb-6 ${classes.labelValuePair}`}>
                            <div>
                                <Typography variant="label">{label}</Typography>
                            </div>
                            <div className="pt-1">
                                <Typography>{value}</Typography>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};
